import React, { useState } from 'react';

const companyName = 'WhubIndia';
const productNames = 'WEWEAR WELLNESS / WHUB WELLNESS';
const contactEmail = 'support@whubindia.com';
const effectiveDate = 'October 10, 2022';

const Section = ({ num, title, children }) => (
  <section className='mb-6'>
    <h3 className='text-lg font-semibold text-red-600 mb-2'>
      {num}. {title}
    </h3>
    <div className='text-gray-800 leading-relaxed text-sm'>{children}</div>
  </section>
);

export default function TermsAndConditions() {
  const [lang, setLang] = useState('en');

  return (
    <div className='bg-gray-50 py-5 md:py-10 md:px-4'>
      <div className='max-w-5xl mx-auto bg-white overflow-hidden'>
        <div className='px-8 py-8 md:py-10'>
          {/* Header */}
          <div className='flex items-start justify-between mb-6'>
            <div>
              <h1 className='text-4xl md:text-5xl font-extrabold text-red-600 leading-tight'>
                Terms &amp; Conditions
              </h1>
              <p className='text-sm text-gray-500 mt-3'>Last updated: {effectiveDate}</p>
            </div>

            {/* Language toggle */}
            <div className='ml-6'>
              <div className='inline-flex rounded-md bg-gray-100 p-1'>
                <button
                  onClick={() => setLang('en')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    lang === 'en' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setLang('ta')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    lang === 'ta' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
                  }`}
                >
                  தமிழ்
                </button>
              </div>
            </div>
          </div>

          {/* Intro / Two column layout for wide screens */}
          <div className='md:grid md:grid-cols-3 md:gap-8'>
            <div className='md:col-span-2'>
              {lang === 'en' ? (
                <>
                  <p className='text-gray-700 text-sm mb-6'>
                    This Terms &amp; Conditions govern your access to and use of the {productNames}{' '}
                    mobile applications and related services (collectively, the "Services") provided
                    by <strong>{companyName}</strong>. By installing, accessing or using the
                    Services you agree to be bound by these Terms. If you do not agree, do not use
                    the Services.
                  </p>

                  <Section num='1' title='Acceptance and Scope'>
                    <p>
                      These Terms, together with any documents expressly incorporated by reference
                      (including our Privacy Policy), constitute the entire agreement between you
                      and {companyName} regarding your use of the Services. These Terms apply to all
                      users, visitors, registered accounts, and other persons who access or use the
                      Services.
                    </p>
                  </Section>

                  <Section num='2' title='Accounts and Registration'>
                    <p>
                      To use certain features you must create an account. You warrant that the
                      information you provide is accurate and current, and you agree to maintain and
                      promptly update such information. You are responsible for all activity that
                      occurs under your account and must keep your password and credentials
                      confidential.
                    </p>
                  </Section>

                  <Section num='3' title='Intellectual Property'>
                    <p>
                      All content, trademarks, logos, designs, source code, graphics, and text made
                      available through the Services are the property of {companyName} or its
                      licensors and are protected by intellectual property laws. You may not copy,
                      reproduce, distribute or create derivative works without prior written
                      consent.
                    </p>
                  </Section>

                  <Section num='4' title='Privacy and Data'>
                    <p>
                      Our collection and processing of personal information is governed by the
                      Privacy Policy. By using the Services you consent to the collection, use and
                      disclosure of your information as described in that policy. You should not
                      submit sensitive medical data unless you have read and accepted the Privacy
                      Policy and any applicable consents.
                    </p>
                  </Section>

                  <Section num='5' title='Disclaimer and Medical / Professional Advice'>
                    <p>
                      The Services provide general wellness information and tools. This information
                      is educational only and is not a substitute for professional medical advice,
                      diagnosis or treatment. Never disregard medical advice, or delay seeking it
                      because of something you have read in the Services. {companyName} is not
                      responsible for any decisions you make based on the content.
                    </p>
                  </Section>

                  <Section num='6' title='Limitation of Liability'>
                    <p>
                      To the maximum extent permitted by law, {companyName} and its affiliates will
                      not be liable for any indirect, incidental, special, consequential or punitive
                      damages, or any loss of profits or revenues arising from or related to your
                      use of the Services.
                    </p>
                  </Section>

                  <Section num='7' title='Termination'>
                    <p>
                      We may suspend or terminate your access to the Services if you breach these
                      Terms, engage in illegal activity, or otherwise misuse the Services. On
                      termination, your right to use the Services ceases immediately.
                    </p>
                  </Section>

                  <Section num='8' title='Governing Law and Dispute Resolution'>
                    <p>
                      These Terms are governed by the laws of India. You agree that the courts
                      located in India have exclusive jurisdiction to resolve disputes arising from
                      these Terms, subject to any mandatory statutory requirements.
                    </p>
                  </Section>

                  <Section num='9' title='Changes to Terms'>
                    <p>
                      We may revise these Terms from time to time. Where required by law, we will
                      provide notice of material changes. Continued use of the Services after
                      revision constitutes acceptance of the revised Terms.
                    </p>
                  </Section>

                  <Section num='10' title='Contact Information'>
                    <p>
                      If you have questions about these Terms, please contact us at{' '}
                      <a href={`mailto:${contactEmail}`} className='text-red-600 underline'>
                        {contactEmail}
                      </a>
                      .
                    </p>
                  </Section>

                  <p className='text-xs text-gray-500 mt-6'>
                    Note: Portions of the Services may be subject to additional terms (for example,
                    third-party integrations, app store policies). In case of conflict, those
                    additional terms may apply.
                  </p>
                </>
              ) : (
                // Tamil content (concise, professional)
                <>
                  <p className='text-gray-700 text-sm mb-6'>
                    இந்த விதிமுறைகள் {productNames} பயன்பாடுகளைப் பயன்படுத்தியதற்கான நிபந்தனைகள்
                    ஆகும். இவை <strong>{companyName}</strong> வழங்கும் சேவைகளுக்கு பொருந்தும்.
                    நீங்கள் சேவையை பயன்படுத்தினால், நீங்கள் இவற்றை ஏற்றுக்கொள்கிறீர்கள்.
                  </p>

                  <Section num='1' title='ஏற்கப்பட்டது மற்றும் வரம்பு'>
                    <p>
                      இந்த நிபந்தனைகள் மற்றும் அவற்றுடன் இணைக்கப்பட்ட கொள்கைகள் உங்கள் மற்றும்{' '}
                      {companyName}
                      இடையேயான முழுமையான ஒப்பந்தமாகும். சேவை பயன்படுத்துபவர்கள் எல்லோருக்கும் இவை
                      பொருந்தும்.
                    </p>
                  </Section>

                  <Section num='2' title='கணக்கு மற்றும் பதிவு'>
                    <p>
                      சில அம்சங்களை பயன்படுத்த கணக்கு உருவாக்க வேண்டும். நீங்கள் வழங்கும் தகவல்
                      சரியானதும் புதுப்பிக்கப்பட்டதும் இருக்க வேண்டும். உங்கள் கணக்கிற்கும்
                      கடவுச்சொல்லுக்குமான பொறுப்பு உங்களுக்கே இருக்கும்.
                    </p>
                  </Section>

                  <Section num='3' title='அறிவுசார் சொத்து'>
                    <p>
                      பயன்பாட்டில் உள்ள அனைத்து உள்ளடக்கமும் {companyName} அல்லது அதன்
                      உரிமையாளர்களின் சொத்தை ஆகும். அனுமதியின்றி நகலெடுக்க அல்லது பகிர் கூடாது.
                    </p>
                  </Section>

                  <Section num='4' title='தனியுரிமை மற்றும் தரவு'>
                    <p>
                      உங்கள் தனிப்பட்ட தகவல்களின் சேமிப்பு மற்றும் பயன்பாடு நமது Privacy Policy
                      மூலம் நிர்ணயிக்கப்படும். அதைப் படித்து சம்மதிக்கவும்.
                    </p>
                  </Section>

                  <Section num='5' title='பொறுப்ப்விதிமுறை'>
                    <p>
                      இந்த பயன்பாடு மருத்துவ பரிந்துரையாகக் கருதப்படக்கூடாது. மருத்துவ ஆலோசனையைத்
                      தேட வேண்டாம் என்றால் இது அடிப்படையிலான தகவல்களை வழங்கும் மட்டுமே.
                    </p>
                  </Section>

                  <Section num='6' title='பொருந்திய சட்டம்'>
                    <p>
                      இநிபந்தனைகள் இந்தியச் சட்டத்தினால் ஆளப்பட்டது. டிஸ்ப்யூட் ஏற்பட்டால் இந்திய
                      நீதிமன்றங்கள் உடனடியாக செயல்படும்.
                    </p>
                  </Section>

                  <Section num='7' title='தொடர்புக்கு'>
                    <p>
                      கேள்விகள் இருப்பின் எங்களுக்கு{' '}
                      <a href={`mailto:${contactEmail}`} className='text-red-600 underline'>
                        {contactEmail}
                      </a>
                      மூலம் தொடர்புகொள்ளவும்.
                    </p>
                  </Section>
                </>
              )}
            </div>

            {/* Right column - quick index / highlights */}
            <aside className='mt-6 md:mt-0 md:col-span-1'>
              <div className='sticky top-8'>
                <div className='bg-gray-50 border border-gray-100 rounded-lg p-4'>
                  <h4 className='text-sm font-semibold text-gray-700 mb-3'>Quick index</h4>
                  <ol className='list-none space-y-2 text-sm text-gray-600'>
                    <li>
                      <span className='inline-block w-2 h-2 bg-rose-600 rounded-full mr-2 align-middle' />
                      Use of Services
                    </li>
                    <li>
                      <span className='inline-block w-2 h-2 bg-rose-600 rounded-full mr-2 align-middle' />
                      Accounts & Security
                    </li>
                    <li>
                      <span className='inline-block w-2 h-2 bg-rose-600 rounded-full mr-2 align-middle' />
                      Privacy & Data
                    </li>
                    <li>
                      <span className='inline-block w-2 h-2 bg-rose-600 rounded-full mr-2 align-middle' />
                      Limitation of Liability
                    </li>
                    <li>
                      <span className='inline-block w-2 h-2 bg-rose-600 rounded-full mr-2 align-middle' />
                      Governing Law
                    </li>
                  </ol>

                  <div className='mt-4 text-xs text-gray-500'>
                    <div>Company: {companyName}</div>
                    <div>
                      Contact:{' '}
                      <a href={`mailto:${contactEmail}`} className='text-red-600 underline'>
                        {contactEmail}
                      </a>
                    </div>
                    <div className='mt-2'>Effective: {effectiveDate}</div>
                  </div>
                </div>

                <div className='mt-4 text-xs text-gray-400'>
                  <p>Printable / readable layout. Use browser print to produce a legal copy.</p>
                </div>
              </div>
            </aside>
          </div>

          {/* Footer */}
          <div className='mt-10 border-t pt-6'>
            <p className='text-xs text-gray-500'>
              These Terms do not create any third-party beneficiary rights. If any provision is
              found invalid, the remaining provisions will remain in effect.
            </p>
            <p className='text-xs text-gray-400 mt-2'>
              © {new Date().getFullYear()} {companyName}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
